import { deleteSpecificPlayers, putUserSbcSolution } from "@/api/privateApi";
import { useExtensionUpdateNotice } from "@/components/features/ClubImport/useExtensionUpdateNotice";
import { SbcSheetButton } from "@/components/organisms/SolutionView/SbcSheetButton";
import { SolutionActions } from "@/components/organisms/SolutionView/SolutionActions";
import { SolutionSidePanel } from "@/components/organisms/SolutionView/SolutionSidePanel";
import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { HeroSection } from "@/containers/HeroSection";
import { useGoBack } from "@/hooks/useGoBack";
import { useScrollLock } from "@/hooks/useScrollLock";
import { AlternativesPanel } from "@/pages/Sbcs/components/Alternatives/AlternativesPanel";
import { AlternativesPill } from "@/pages/Sbcs/components/Alternatives/AlternativesPill";
import { AlternativesSheet } from "@/pages/Sbcs/components/Alternatives/AlternativesSheet";
import {
  checkExtensionAvailability,
  selectClubImportState,
  selectExtensionCheckReady,
  selectExtensionSupportsLatestFeatures,
} from "@/redux/clubImport/clubImportSlice";
import { setIsFloatingMenuHidden } from "@/redux/globalState/globalStateSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import { streamlineSolveSettingsSelector } from "@/redux/sbcs/sbcFiltersSlice";
import { SbcSet, markSbcSetSolved } from "@/redux/sbcs/sbcSetsSlice";
import { getSbcSelector } from "@/redux/sbcs/sbcsSlice";
import { AppDispatch, IRootState } from "@/redux/store";
import {
  canUseClubImportSelector,
  fetchUser,
  getUserSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useStreamlineSolution } from "@repo/shared/hooks";
import { errorMessage, useTranslation } from "@repo/shared/i18n";
import {
  fetchStreamlineSolution,
  streamlineSetSelector,
  streamlineStateSelector,
} from "@repo/shared/redux";
import {
  STREAMLINE_TAGS,
  heldClubCards,
  isBracketDone,
  isMarked,
  streamlineTotals,
  togglingFinishesBracket,
} from "@repo/shared/streamline";
import { StreamlineSolution } from "@repo/shared/types";
import { sbcSetPath } from "@repo/shared/utils";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { AddPlayerSheet } from "./components/AddPlayerSheet";
import { CompletionPanel } from "./components/CompletionPanel";
import {
  StreamlineBracketRow,
  StreamlineBracketRowSkeleton,
} from "./components/StreamlineBracketRow";
import { StreamlineDesktopHeader } from "./components/StreamlineDesktopHeader";
import { StreamlineMobileHeader } from "./components/StreamlineMobileHeader";
import { toAlternativeRow } from "./components/alternativeRows";

/* What the screen shows before the first solve lands: an empty solution
   under the same headers, in their loading state, over skeleton rows. */
const PENDING_SOLUTION: StreamlineSolution = {
  id: "",
  tags: [],
  totalValue: 0,
  transferCost: 0,
  transferCount: 0,
  ownedCount: 0,
  isRecommended: false,
  brackets: [],
};
const PENDING_TOTALS = streamlineTotals(PENDING_SOLUTION, 0, {});
const SKELETON_ROWS = 3;

/**
 * The FC 27 Streamline solution screen: which players to place, what they cost,
 * how far along the points target is, and which other solutions exist.
 *
 * The set comes from `POST /streamlined-sbcs/:setId/solutions`, fetched once on
 * mount; the state, the maths and every component under it work off the shared
 * types the adapter produces. `sbcSet` is the SBC set the URL names, loaded:
 * SbcChallengePage waits for it.
 */
const StreamlineSbcPage = ({ sbcSet }: { sbcSet: SbcSet }) => {
  const { t } = useTranslation("sbcs");
  const { setId, sbcId } = useParams();
  const navigate = useNavigate();
  const setPagePath = sbcSetPath(sbcSet);
  const goBack = useGoBack({ fallback: setPagePath });
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const [markedComplete, setMarkedComplete] = useState(false);
  const { user } = useSelector(getUserSelector);
  /* The challenge, for its requirements in the rail and the reward sheet. */
  const sbc = useSelector(getSbcSelector(Number(sbcId), Number(setId)));

  const set = useSelector(streamlineSetSelector);
  const { setStatus, error } = useSelector(streamlineStateSelector);

  const canUseClubImport = useSelector(canUseClubImportSelector);
  const { extensionEnabledStatus } = useSelector(selectClubImportState);
  const extensionSupportsLatest = useSelector(
    selectExtensionSupportsLatestFeatures,
  );
  const showSendToExtension =
    canUseClubImport &&
    extensionEnabledStatus === "enabled" &&
    extensionSupportsLatest;
  const extensionCheckReady = useSelector(selectExtensionCheckReady);
  /* Streamlined insert shipped in extension 2.4.17: an extension behind the
     latest release gets the update pop-up before sending. */
  const withExtensionUpdateNotice = useExtensionUpdateNotice();

  useEffect(() => {
    if (!extensionCheckReady) return;
    dispatch(checkExtensionAvailability({ user }));
  }, [dispatch, extensionCheckReady, user]);

  /* Read once per open: a Customize change waits for Solve again. */
  const store = useStore<IRootState>();
  const settings = useSelector(streamlineSolveSettingsSelector);
  useEffect(() => {
    dispatch(
      fetchStreamlineSolution({
        setId: Number(setId),
        sbcId: Number(sbcId),
        settings: streamlineSolveSettingsSelector(store.getState()),
      }),
    );
  }, [dispatch, store, setId, sbcId]);

  const {
    solution,
    solutions,
    marks,
    totals,
    alternativesOpen,
    minPoints,
    options,
    optionsStatus,
    onToggleBracket,
    onToggleEntry,
    onToggleEntries,
    onSwapPlayer,
    onToggleAlternatives,
    onCloseAlternatives,
    onSelectSolution,
    completed,
    onSetAddedPlayers,
    onCommitProgress,
    onSolveAgain,
  } = useStreamlineSolution(set, settings);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  /* What the search sheet owns: the cards that came in through it, not the
     marks a re-solve froze. Memoised: the sheet seeds itself from it. */
  const added = useMemo(
    () => completed.filter((entry) => entry.added),
    [completed],
  );
  /*
    Rows folded to their header, by bracket id. A row folds when the user
    finishes it here: the Done control, or the chip on its last open card. An
    added card that fills a row on its own marks it Done but leaves it open;
    unchecking Done opens it again. Bracket ids repeat across solutions, so
    the set is cleared whenever the solution on screen changes.
  */
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  useEffect(() => setCollapsed({}), [set, solution?.id]);
  const fold = (bracketId: string, value: boolean) =>
    setCollapsed((prev) => ({ ...prev, [bracketId]: value }));
  const totalsDone = totals?.isDone;
  /* The completion panel floats over the rows and can be waved away; the
     next change to a row brings it back once the target is reached again. */
  const [completionDismissed, setCompletionDismissed] = useState(false);
  const completionOpen = Boolean(totalsDone) && !completionDismissed;

  /* A refused solve goes back to the set page, where Customize and Solve
     are. Refused for want of an imported club or a Credit, the set page's
     Solve pitch says what is missing (the 402 backstop dialog is already up
     in the second case); every other failure is told in an alert, with the
     squad solver's copy (errors:solver.*). */
  useEffect(() => {
    if (setStatus !== "rejected") return;
    if (error?.code !== "CLUB_IMPORT_REQUIRED" && error?.status !== 402)
      dispatch(
        addAlert({
          description: errorMessage(error?.code, "solver"),
          type: "error",
        }),
      );
    navigate(setPagePath, { replace: true });
  }, [setStatus, error, dispatch, navigate, setPagePath]);

  useEffect(() => {
    if (set) window.scrollTo(0, 0);
  }, [set]);

  useScrollLock(completionOpen);

  /* The progress line sticks to the top on mobile, where the floating burger
     would sit behind it; the page does without the burger. */
  useEffect(() => {
    dispatch(setIsFloatingMenuHidden(true));
    return () => void dispatch(setIsFloatingMenuHidden(false));
  }, [dispatch]);

  const name = set?.name ?? sbcSet.name;
  const hero = (
    <HeroSection
      helmetTitle={t("setPage.mobileTitle", { game: GAME_NAME, name })}
      helmetDescription={sbcSet.description}
      helmetCanonicalLink={canonicalUrl(setPagePath)}
      shouldBeInTheDocumentFlow
      back={goBack}
    />
  );

  /* On the way back to the set page. */
  if (setStatus === "rejected")
    return <main className="min-h-screen flex-1 text-white">{hero}</main>;

  /* Stores the club cards on screen (marked done and added ones too) for the
     extension to select in EA's OneClick work area and drops them from the
     imported club. Market cards stay out, and the set only counts as
     completed when there are none: those still have to be bought. Unlimited
     repeatable sets are never completed, same as on the sets list. */
  const onSendToWebApp = async () => {
    if (!set || !solution) return;
    if (!user.uuid) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "complete-sbc",
        }),
      );
      return;
    }
    const entries = solution.brackets.flatMap((bracket) => bracket.players);
    const clubEntries = entries.filter(
      (entry) => entry.source !== "MARKET" && entry.eaPlayerId,
    );
    const completes =
      entries.every((entry) => entry.source !== "MARKET") &&
      sbcSet.repeatabilityMode !== "UNLIMITED";
    await putUserSbcSolution(set.id, Number(sbcId), {
      players: clubEntries.map((entry) => ({
        resourceId: entry.player.resourceId,
        eaPlayerId: entry.eaPlayerId,
      })),
    });
    if (completes)
      dispatch(markSbcSetSolved({ setId: set.id, didComplete: true }));
    if (clubEntries.length > 0) {
      await deleteSpecificPlayers(
        clubEntries.map((entry) => entry.eaPlayerId as number),
      );
      dispatch(fetchUser({ didLogin: false }));
    }
    setMarkedComplete(true);
    setCompletionDismissed(true);
    dispatch(
      addAlert({
        title: completes
          ? t("alerts.completed", { name: set.name })
          : t("sbcSolution:sentToExtension"),
        description:
          clubEntries.length === 0
            ? ""
            : t("alerts.playersRemovedFromImport", {
                count: clubEntries.length,
              }),
        type: "success",
      }),
    );
  };
  const isMarkedComplete = markedComplete || Boolean(sbcSet.solved);
  /* The completion pop-up marks the set complete if nothing has yet, then
     leaves for the SBC list. */
  const onContinue = () => {
    if (!set) return;
    if (!user.uuid) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "complete-sbc",
        }),
      );
      return;
    }
    if (!isMarkedComplete) {
      if (sbcSet.repeatabilityMode !== "UNLIMITED")
        dispatch(markSbcSetSolved({ setId: set.id, didComplete: true }));
      dispatch(
        addAlert({
          description: t("alerts.completed", { name: set.name }),
          type: "success",
        }),
      );
    }
    navigate("/sbc");
  };

  /* The first solve and a re-solve look the same: the headers hold their
     place with skeleton values and the rows give way to skeleton rows. */
  const shownSolution = solution ?? PENDING_SOLUTION;
  const shownTotals = totals ?? PENDING_TOTALS;
  const loading = !solution || setStatus === "pending";

  const reward = sbcSet.reward ?? sbc?.reward;
  const requirements = sbc?.requirements ?? [];
  /* The normal solution view's actions, without a complete button unless
     the extension can take the cards: the completion panel does that job. */
  const actions = (
    <SolutionActions
      isCompleted={isMarkedComplete}
      isSolving={loading}
      showSendToExtension={showSendToExtension}
      compact={isMobile}
      extra={
        <SbcSheetButton
          reward={reward}
          description={sbcSet.description}
          requirements={requirements}
        />
      }
      alternatives={
        isMobile ? (
          <AlternativesPill compact onClick={onToggleAlternatives} />
        ) : (
          <AlternativesPanel
            rows={solutions.map(toAlternativeRow)}
            allTags={STREAMLINE_TAGS}
            currentId={shownSolution.id}
            onSelect={onSelectSolution}
            showTime
            stretch
          />
        )
      }
      onSolveAgain={onSolveAgain}
      onComplete={
        showSendToExtension
          ? () => withExtensionUpdateNotice(onSendToWebApp)
          : undefined
      }
    />
  );

  /* Closing the search confirms it: a changed set of added cards is a new
     solve, or done outright; either way the panel may show again. */
  const onClosePlayers: typeof onSetAddedPlayers = (picked) => {
    setAddPlayerOpen(false);
    setCompletionDismissed(false);
    onSetAddedPlayers(picked);
  };
  const onCommitTotal: typeof onCommitProgress = (total) => {
    setCompletionDismissed(false);
    onCommitProgress(total);
  };

  /* Mid-solve the rows holding added cards move to the top, where the eye
     is; once the solve lands every row is back in points order. */
  const brackets = solution
    ? loading
      ? [...solution.brackets].sort(
          (a, b) =>
            Number(b.players.some((e) => isMarked(marks, e.entryId))) -
            Number(a.players.some((e) => isMarked(marks, e.entryId))),
        )
      : solution.brackets
    : [];
  const rows = solution
    ? brackets.map((bracket, index) => (
        <StreamlineBracketRow
          key={bracket.bracketId}
          bracket={bracket}
          index={index}
          solution={solution}
          solutions={solutions}
          options={options}
          optionsLoading={optionsStatus === "pending"}
          marks={marks}
          loading={loading}
          onToggleBracket={() => {
            setCompletionDismissed(false);
            fold(bracket.bracketId, !isBracketDone(bracket, marks));
            onToggleBracket(bracket);
          }}
          onToggleEntry={(entryId) => {
            setCompletionDismissed(false);
            if (togglingFinishesBracket(bracket, marks, [entryId]))
              fold(bracket.bracketId, true);
            onToggleEntry(entryId);
          }}
          onToggleEntries={(entryIds) => {
            setCompletionDismissed(false);
            if (togglingFinishesBracket(bracket, marks, entryIds))
              fold(bracket.bracketId, true);
            onToggleEntries(entryIds);
          }}
          onSwapPlayer={onSwapPlayer}
          collapsed={Boolean(collapsed[bracket.bracketId])}
          onToggleCollapsed={() =>
            fold(bracket.bracketId, !collapsed[bracket.bracketId])
          }
          scroll={isMobile}
        />
      ))
    : Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <StreamlineBracketRowSkeleton
          key={index}
          index={index}
          scroll={isMobile}
        />
      ));

  return (
    <main className="min-h-screen flex-1 text-white ">
      {/* The app's own page header: back arrow + title on desktop, and the
          persistent mobile top nav on touch devices. */}
      {hero}

      {isMobile ? (
        <>
          <StreamlineMobileHeader
            actions={actions}
            totals={shownTotals}
            solution={shownSolution}
            minPoints={minPoints}
            onCommitProgress={onCommitTotal}
            onAddPlayer={() => setAddPlayerOpen(true)}
            addedCount={added.length}
            loading={loading}
          />
          <section className="pt-0.5 pb-8">{rows}</section>
        </>
      ) : (
        /* The normal solution view's rail on the left, the progress line
           and the rows beside it. The column runs the rail's height, so the
           progress line sticks for the whole page. */
        <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-content items-stretch">
          <SolutionSidePanel
            facts={shownTotals}
            recommended={shownSolution.isRecommended}
            showEstimate
            tags={shownSolution.tags}
            loading={loading}
            actions={actions}
            setReward={reward}
            setDescription={sbcSet.description}
            requirements={requirements}
          />
          <div className="min-w-0 flex-1">
            <StreamlineDesktopHeader
              totals={shownTotals}
              minPoints={minPoints}
              onCommitProgress={onCommitTotal}
              onAddPlayer={() => setAddPlayerOpen(true)}
              addedCount={added.length}
              loading={loading}
            />
            <section className="pt-0.5 pb-8">{rows}</section>
          </div>
        </div>
      )}

      {totals && (
        <CompletionPanel
          isOpen={completionOpen}
          totals={totals}
          onContinue={onContinue}
          isMarkedComplete={isMarkedComplete}
          onClose={() => setCompletionDismissed(true)}
        />
      )}

      {set && (
        <AddPlayerSheet
          isOpen={addPlayerOpen}
          onClose={onClosePlayers}
          added={added}
          heldEaPlayerIds={heldClubCards(shownSolution)}
        />
      )}

      {isMobile && (
        <AlternativesSheet
          isOpen={alternativesOpen}
          onClose={onCloseAlternatives}
          rows={solutions.map(toAlternativeRow)}
          allTags={STREAMLINE_TAGS}
          currentId={shownSolution.id}
          onSelect={onSelectSolution}
          showTime
        />
      )}
    </main>
  );
};

export default StreamlineSbcPage;
